import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const MobileFilters = ({ 
  isOpen, 
  onClose, 
  filters, 
  onFiltersChange, 
  onApplyFilters, 
  categories = [] 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      search: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      sort: 'created_at',
      order: 'desc',
      brand: '',
      minRating: '',
      inStock: false,
      tags: ''
    };
    setLocalFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  return (
    <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
      isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
    }`}>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal - Full screen on mobile */}
      <div className={`fixed inset-0 bg-white z-50 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">מסננים</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Filters Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {/* Category Filter */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">קטגוריה</h3>
              <select
                value={localFilters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">כל הקטגוריות</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">טווח מחירים</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  placeholder="מ-"
                  value={localFilters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                />
                <input
                  type="number"
                  placeholder="עד"
                  value={localFilters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                />
              </div>
            </div>

            {/* Brand Filter */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">מותג</h3>
              <input
                type="text"
                placeholder="הכנס שם מותג"
                value={localFilters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              />
            </div>

            {/* Rating Filter */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">דירוג מינימלי</h3>
              <select
                value={localFilters.minRating}
                onChange={(e) => handleFilterChange('minRating', e.target.value)}
                className="w-full p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">כל הדירוגים</option>
                <option value="4">4+ כוכבים</option>
                <option value="3">3+ כוכבים</option>
                <option value="2">2+ כוכבים</option>
                <option value="1">1+ כוכבים</option>
              </select>
            </div>

            {/* In Stock Filter */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.inStock}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="mr-3 text-base text-gray-700">רק מוצרים במלאי</span>
              </label>
            </div>

            {/* Sort */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">מיון</h3>
              <select
                value={`${localFilters.sort}-${localFilters.order}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-');
                  handleFilterChange('sort', sort);
                  handleFilterChange('order', order);
                }}
                className="w-full p-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="created_at-desc">חדש ביותר</option>
                <option value="created_at-asc">ישן ביותר</option>
                <option value="name-asc">שם א-ת</option>
                <option value="name-desc">שם ת-א</option>
                <option value="price_b2c-asc">מחיר נמוך לגבוה</option>
                <option value="price_b2c-desc">מחיר גבוה לנמוך</option>
                <option value="rating_average-desc">דירוג גבוה</option>
                <option value="sales_count-desc">הנמכרים ביותר</option>
              </select>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-gray-200 space-y-4 bg-gray-50">
            <button
              onClick={handleApply}
              className="w-full py-4 px-4 bg-primary-600 text-white rounded-xl font-semibold text-base hover:bg-primary-700 transition-colors shadow-lg"
            >
              החל מסננים
            </button>
            <button
              onClick={handleReset}
              className="w-full py-4 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold text-base hover:bg-gray-200 transition-colors"
            >
              איפוס מסננים
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileFilters;
